import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
    FaBiking,
    FaCode,
    FaMusic,
    FaBook,
    FaGamepad,
    FaMountain,
    FaFilm,
    FaChevronLeft,
    FaTimes
} from 'react-icons/fa';

const Hobbies = ({ darkMode, onClose }) => {
    const { t } = useTranslation();
    const [activeHobby, setActiveHobby] = useState(null);

    // Datos predeterminados de hobbies
    const defaultHobbies = [
        {
            id: 1,
            icon: <FaBiking className="text-3xl" />,
            name: "Ciclismo",
            description: "Disfruto recorrer nuevas rutas en bicicleta los fines de semana, explorando paisajes naturales y urbanos. Es mi forma favorita de ejercicio y conexión con la naturaleza.",
            color: "bg-green-500",
            details: [
                "Rutas favoritas en Santiago",
                "Participación en eventos ciclistas locales",
                "Mantenimiento de bicicletas como hobby adicional"
            ]
        },
        {
            id: 2,
            icon: <FaCode className="text-3xl" />,
            name: "Programación",
            description: "Más allá del aspecto profesional, disfruto programar proyectos personales y participar en hackathons. Me gusta experimentar con nuevas tecnologías y lenguajes.",
            color: "bg-blue-500",
            details: [
                "Participación en competencias de programación",
                "Contribuciones a proyectos open source",
                "Desarrollo de pequeñas aplicaciones para uso personal"
            ]
        },
        {
            id: 3,
            icon: <FaMusic className="text-3xl" />,
            name: "Escuchar Música",
            description: "La música es mi compañera constante. Disfruto explorar diferentes géneros y descubrir nuevos artistas. Tengo una colección de playlists para diferentes estados de ánimo y actividades.",
            color: "bg-purple-500",
            details: [
                "Géneros favoritos: rock alternativo, jazz y música electrónica",
                "Coleccionista digital de álbumes clásicos",
                "Asistencia a conciertos locales cuando es posible"
            ]
        },
        {
            id: 4,
            icon: <FaBook className="text-3xl" />,
            name: "Estudiar",
            description: "Me apasiona el aprendizaje continuo, no solo en tecnología sino en diversos campos del conocimiento. Dedico tiempo regularmente a cursos online, libros técnicos y artículos científicos.",
            color: "bg-yellow-500",
            details: [
                "Cursos completados en plataformas como Coursera y edX",
                "Lectura regular de publicaciones técnicas",
                "Participación en grupos de estudio"
            ]
        },
        {
            id: 5,
            icon: <FaGamepad className="text-3xl" />,
            name: "Videojuegos",
            description: "Los videojuegos son mi forma favorita de relajación. Disfruto especialmente los juegos de estrategia, RPGs y títulos independientes con narrativas innovadoras.",
            color: "bg-red-500",
            details: [
                "Juegos favoritos: series Civilization, The Witcher, Hollow Knight",
                "Interés en el desarrollo indie de videojuegos",
                "Participación ocasional en game jams"
            ]
        },
        {
            id: 6,
            icon: <FaMountain className="text-3xl" />,
            name: "Senderismo",
            description: "Descubrir nuevas rutas y conectar con la naturaleza es una de mis actividades favoritas. Me gusta documentar mis caminatas y explorar nuevos senderos en República Dominicana.",
            color: "bg-green-700",
            details: [
                "Rutas favoritas en la cordillera Septentrional",
                "Fotografía de paisajes como hobby complementario",
                "Planificación de rutas usando tecnología GPS"
            ]
        },
        {
            id: 7,
            icon: <FaFilm className="text-3xl" />,
            name: "Cine",
            description: "Soy un entusiasta del cine, especialmente de ciencia ficción, documentales tecnológicos y películas independientes. Me gusta analizar narrativas y técnicas cinematográficas.",
            color: "bg-indigo-500",
            details: [
                "Directores favoritos: Christopher Nolan, Denis Villeneuve",
                "Asistencia regular a festivales de cine locales",
                "Interés en la intersección entre tecnología y narrativas visuales"
            ]
        }
    ];

    // Obtener datos de hobbies desde las traducciones o usar valores predeterminados
    let hobbies;
    try {
        const translatedHobbies = t('hobbies.items', { returnObjects: true });
        hobbies = Array.isArray(translatedHobbies) && translatedHobbies.length > 0 ? translatedHobbies : defaultHobbies;
    } catch (error) {
        console.error("Error al obtener datos de hobbies:", error);
        hobbies = defaultHobbies;
    }

    const setHobbyDetail = (id) => {
        if (activeHobby === id) {
            setActiveHobby(null);
        } else {
            setActiveHobby(id);
        }
    };

    // Variantes para animación
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5 }
        }
    };

    const detailVariants = {
        hidden: { opacity: 0, height: 0 },
        visible: {
            opacity: 1,
            height: 'auto',
            transition: { duration: 0.3 }
        }
    };

    return (
        <div className={`fixed inset-0 z-50 overflow-y-auto ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className="relative min-h-screen p-8">
                {/* Header con botones de navegación */}
                <div className="flex justify-between items-center mb-8">
                    <button
                        onClick={onClose}
                        className={`flex items-center px-4 py-2 rounded-lg ${
                            darkMode
                                ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        } transition-colors`}
                    >
                        <FaChevronLeft className="mr-2" />
                        {t('hobbies.backToPortfolio', 'Volver al portafolio')}
                    </button>

                    <button
                        onClick={onClose}
                        className={`p-2 rounded-full ${
                            darkMode
                                ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        } transition-colors`}
                        aria-label="Close"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Título de la sección */}
                <h2 className={`text-4xl font-bold mb-6 text-center ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                    {t('hobbies.title', 'Mis Intereses y Pasatiempos')}
                </h2>

                <p className={`text-lg mb-10 text-center max-w-2xl mx-auto ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('hobbies.subtitle', 'Más allá de la programación, estas son algunas de las actividades que disfruto en mi tiempo libre.')}
                </p>

                {/* Grid de hobbies */}
                <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {hobbies.map((hobby) => (
                        <motion.div
                            key={hobby.id}
                            variants={itemVariants}
                            className={`rounded-xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} shadow-lg overflow-hidden transition-all`}
                            onClick={() => setHobbyDetail(hobby.id)}
                        >
                            <div className="flex items-start">
                                <div className={`p-3 rounded-lg ${hobby.color} text-white mr-4`}>
                                    {hobby.icon}
                                </div>
                                <div>
                                    <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                        {hobby.name}
                                    </h3>
                                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {hobby.description}
                                    </p>

                                    {activeHobby === hobby.id && hobby.details && (
                                        <motion.div
                                            variants={detailVariants}
                                            initial="hidden"
                                            animate="visible"
                                            className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
                                        >
                                            <ul className={`list-disc pl-5 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                {hobby.details.map((detail, index) => (
                                                    <li key={index} className="mb-1">{detail}</li>
                                                ))}
                                            </ul>
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Footer */}
                <div className="text-center mt-16">
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('hobbies.footer', '¿Compartimos algún interés? ¡Cuéntame sobre ello en la sección de contacto!')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Hobbies;